import { useRef } from "react";
import Markdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ExplorerItem } from "../hooks/useLawExplorer";
import { useLegalRefs } from "../hooks/useLegalRefs";
import { ArticleNotes, type ArticleNotesHandle } from "./ArticleNotes";

interface LawExplorerViewerProps {
  item: ExplorerItem | null;
  isLoading: boolean;
  error: string | null;
  onNavigate: (type: "article" | "section", id: string) => void;
  onRetry?: () => void;
}

const REMARK_REHYPE_OPTIONS = { footnoteLabel: "Notes" } as const;

function isFootnoteRefAnchor(props: Record<string, unknown>): boolean {
  return (
    props["data-footnote-ref"] !== undefined ||
    props.dataFootnoteRef === true ||
    props.dataFootnoteRef === ""
  );
}

function isFootnotesSection(props: Record<string, unknown>): boolean {
  return (
    props["data-footnotes"] !== undefined ||
    props.dataFootnotes === true ||
    props.dataFootnotes === ""
  );
}

function footnoteTargetId(href?: string): string | null {
  if (!href || !href.startsWith("#")) return null;
  const id = href.slice(1);
  // GFM refs point at #user-content-fn-N (or #fn-N without clobber prefix)
  if (id.includes("fn-") && !id.includes("fnref-")) return id;
  return null;
}

interface MarkdownWithNotesProps {
  content: string;
  onNavigate: (type: "article" | "section", id: string) => void;
}

function MarkdownWithNotes({ content, onNavigate }: MarkdownWithNotesProps) {
  const notesRef = useRef<ArticleNotesHandle>(null);

  const renderLink: NonNullable<Components["a"]> = ({ href, children, ...rest }) => {
    if (isFootnoteRefAnchor(rest)) {
      const targetId = footnoteTargetId(href);
      return (
        <a
          href={href}
          data-footnote-ref=""
          className="article-note-ref"
          onClick={(e) => {
            e.preventDefault();
            if (!targetId) return;
            notesRef.current?.expand();
            notesRef.current?.highlight(targetId);
          }}
        >
          {children}
        </a>
      );
    }

    if (!href) {
      return <span>{children}</span>;
    }

    const isKaliLink = href.includes("/conv_coll/id/");
    const isLegiLink = href.includes("/codes/article_lc/") || href.includes("/codes/section_lc/");

    if (isKaliLink) {
      const articleIdMatch = href.match(/\/id\/([^/]+)/);
      const articleId = articleIdMatch?.[1];

      if (articleId) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              onNavigate("article", articleId);
            }}
            className="article-ref"
            title="Voir cet article"
          >
            {children}
          </a>
        );
      }
    }

    if (isLegiLink) {
      const articleIdMatch = href.match(/article_lc\/([^/]+)/) || href.match(/#(LEGIARTI[^/\s]+)/);
      const articleId = articleIdMatch?.[1];

      if (articleId) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              onNavigate("article", articleId);
            }}
            className="article-ref legi-ref"
            title="Voir cet article"
          >
            {children}
          </a>
        );
      }
    }

    // Backrefs inside the notes list — keep default in-page jump
    const extra = rest as Record<string, unknown>;
    if (extra["data-footnote-backref"] !== undefined || extra.dataFootnoteBackref !== undefined) {
      return (
        <a
          href={href}
          data-footnote-backref=""
          className="data-footnote-backref"
          aria-label={typeof rest["aria-label"] === "string" ? rest["aria-label"] : undefined}
        >
          {children}
        </a>
      );
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      remarkRehypeOptions={REMARK_REHYPE_OPTIONS}
      components={{
        a: renderLink,
        section: ({ children, ...props }) => {
          if (isFootnotesSection(props as Record<string, unknown>)) {
            return <ArticleNotes ref={notesRef}>{children}</ArticleNotes>;
          }
          return <section>{children}</section>;
        },
        table: ({ children }) => (
          <div className="table-wrapper">
            <table>{children}</table>
          </div>
        ),
      }}
    >
      {content}
    </Markdown>
  );
}

export function LawExplorerViewer({ item, isLoading, error, onNavigate }: LawExplorerViewerProps) {
  // Process LEGI content to linkify article references (e.g., L1234-13)
  // Hook must be called unconditionally at the top
  const rawContent = item?.type === "article" ? item.contentMarkdown || "" : "";
  const isLegiArticle = item?.type === "article" && item.source === "legi";
  const { processedContent } = useLegalRefs(
    rawContent,
    true, // always complete (not streaming)
    { enabled: isLegiArticle },
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="law-explorer-viewer">
        <div className="law-explorer-loading">
          <div className="law-explorer-loading-spinner" />
          <p>Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="law-explorer-viewer">
        <div className="law-explorer-error">
          <div className="law-explorer-error-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="law-explorer-error-text">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!item) {
    return (
      <div className="law-explorer-viewer">
        <div className="law-explorer-empty">
          <p>Selectionnez un article pour l'afficher</p>
        </div>
      </div>
    );
  }

  // Section view (with ancestry and children)
  if (item.type === "section") {
    const stripSectionPrefix = (titre: string) => {
      return titre.replace(/^Section\s+\d+\s*:\s*/i, "").trim() || titre;
    };

    return (
      <div className="law-explorer-viewer">
        {item.ancestryPath && item.ancestryPath.length > 0 && (
          <nav className="law-explorer-ancestry">
            <ul className="law-explorer-ancestry-tree">
              {item.ancestryPath.map((ancestor, index) => (
                <li
                  key={ancestor.id}
                  className="law-explorer-ancestry-item"
                  style={{ paddingLeft: `${index * 1}rem` }}
                >
                  <button
                    className="law-explorer-ancestry-link"
                    onClick={() => onNavigate("section", ancestor.id)}
                  >
                    {ancestor.titre}
                  </button>
                </li>
              ))}
              <li
                className="law-explorer-ancestry-item law-explorer-ancestry-current"
                style={{ paddingLeft: `${item.ancestryPath.length * 1}rem` }}
              >
                <span>{item.titre}</span>
              </li>
            </ul>
          </nav>
        )}

        <article className="law-explorer-content answer-content">
          <h1 className="law-explorer-title">{item.titre || "Section"}</h1>

          {item.childSections && item.childSections.length > 0 && (
            <section className="law-explorer-pills-section">
              <span className="example-label">Sous-sections</span>
              <div className="law-explorer-pills-grid">
                {item.childSections.map((child) => (
                  <button
                    key={child.id}
                    className="example-query"
                    onClick={() => onNavigate("section", child.id)}
                  >
                    {stripSectionPrefix(child.titre)}
                  </button>
                ))}
              </div>
            </section>
          )}

          {item.childArticles && item.childArticles.length > 0 && (
            <section className="law-explorer-pills-section">
              <span className="example-label">Articles</span>
              <div className="law-explorer-pills-grid">
                {item.childArticles.map((article) => (
                  <button
                    key={article.id}
                    className="example-query"
                    onClick={() => onNavigate("article", article.id)}
                  >
                    {article.num}
                  </button>
                ))}
              </div>
            </section>
          )}

          {item.childArticles && item.childArticles.length > 0 && (
            <div className="law-explorer-articles-content">
              {item.childArticles.map((article) => (
                <section key={article.id} className="law-explorer-article-inline">
                  <h2>{article.num}</h2>
                  <MarkdownWithNotes
                    content={article.contentMarkdown || ""}
                    onNavigate={onNavigate}
                  />
                </section>
              ))}
            </div>
          )}

          {(!item.childSections || item.childSections.length === 0) &&
            (!item.childArticles || item.childArticles.length === 0) && (
              <p className="law-explorer-section-hint">
                Cette section ne contient pas d'articles directement.
                <br />
                <a href={item.legifranceUrl} target="_blank" rel="noopener noreferrer">
                  Voir sur Legifrance
                </a>
              </p>
            )}
        </article>
      </div>
    );
  }

  return (
    <div className="law-explorer-viewer">
      {item.source === "legi" && item.ancestryPath && item.ancestryPath.length > 0 && (
        <nav className="law-explorer-ancestry">
          <ul className="law-explorer-ancestry-tree">
            {item.ancestryPath.map((ancestor, index) => (
              <li
                key={ancestor.id}
                className="law-explorer-ancestry-item"
                style={{ paddingLeft: `${index * 1}rem` }}
              >
                <button
                  className="law-explorer-ancestry-link"
                  onClick={() => onNavigate("section", ancestor.id)}
                >
                  {ancestor.titre}
                </button>
              </li>
            ))}
            <li
              className="law-explorer-ancestry-item law-explorer-ancestry-current"
              style={{ paddingLeft: `${item.ancestryPath.length * 1}rem` }}
            >
              <span>Article {item.num}</span>
            </li>
          </ul>
        </nav>
      )}

      <article className="law-explorer-content answer-content">
        {item.source === "kali" && item.titre && (
          <h1 className="law-explorer-title">{item.titre}</h1>
        )}

        <MarkdownWithNotes content={processedContent} onNavigate={onNavigate} />

        {item.source === "legi" && item.nota && (
          <aside className="law-explorer-nota">
            <h3>Nota</h3>
            <Markdown remarkPlugins={[remarkGfm]} remarkRehypeOptions={REMARK_REHYPE_OPTIONS}>
              {item.nota}
            </Markdown>
          </aside>
        )}
      </article>
    </div>
  );
}
